import type { FuseResultMatch } from 'fuse.js';
import { ArrowRight2 } from 'iconsax-react';
import { type ReactNode, useMemo } from 'react';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import { SearchFriendResultDescription } from '@/components/search/search-friend-result-item';
import { HighlightKeyword } from '@/components/search/search-list-item';
import type { FuzzySearchResult } from '@/hooks/search/use-search-friends';
import { useIsInternalUser } from '@/hooks/users/use-user-info';
import type { Group } from '@/models/group/group-info';
import type { Friend } from '@/models/user/friend';
import cn from '@/utils/merge-class-names';

interface Props {
  item: (
    | Pick<Friend, 'FriendNickName' | 'FriendHeadImg' | 'RemarkName' | 'OnlineDesc' | 'IsOnline' | 'ID'>
    | (Pick<Group, 'GroupName' | 'GroupImg' | 'Info' | 'UserCount' | 'ID'> & { OnlineDesc?: string })
  ) & { FuzzySearch?: FuzzySearchResult };
  arrow?: boolean;
  className?: string;
  suffix?: ReactNode;
  showOnlineDesc?: boolean;
}

export function ContactListItem({ item, arrow = false, className, suffix, showOnlineDesc = true }: Props) {
  const { isInternalUser } = useIsInternalUser();
  const isFriend = 'FriendHeadImg' in item;
  const isOnline = isFriend && item.IsOnline;
  const { titleHighlight, showNicknameInDescription } = useMemo(() => {
    const { FuzzySearch } = item;
    if ('FriendNickName' in item) {
      const { RemarkName, FriendNickName } = item;
      let nicknameResult: FuseResultMatch | undefined;
      let remarknameResult: FuseResultMatch | undefined;
      for (const h of FuzzySearch?.highlightRanges || []) {
        if (h.key === 'FriendNickName' || h.key === 'NickName') {
          nicknameResult = h;
        }
        if (h.key === 'RemarkName') {
          remarknameResult = h;
        }
        if (nicknameResult && remarknameResult) {
          break;
        }
      }
      return {
        titleHighlight:
          (RemarkName && remarknameResult?.indices) ||
          (!RemarkName && FriendNickName && nicknameResult?.indices) ||
          undefined,
        showNicknameInDescription: !!(RemarkName && nicknameResult),
      };
    }
    return {
      titleHighlight: FuzzySearch?.highlightRanges?.[0]?.indices || undefined,
      showNicknameInDescription: false,
    };
  }, [item]);
  return (
    <div className={cn('flex w-full min-w-0 items-center gap-4 rounded-s px-5 py-2 transition-colors', className)}>
      <AvatarWithStatus
        className='size-12'
        imageUrl={isFriend ? item.FriendHeadImg : item.GroupImg}
        label={isFriend ? item.FriendNickName : item.GroupName}
        defaultAvatar={'GroupImg' in item ? 'group' : 'user'}
        isOnline={isInternalUser && isOnline}
        size={[48, 48]}
      />
      <div className='flex w-1/2 flex-1 flex-col items-stretch justify-center'>
        <h2 className='truncate break-all text-h2 text-wrap line-clamp-2'>
          <HighlightKeyword
            text={isFriend ? item.RemarkName || item.FriendNickName : item.GroupName}
            highlight={titleHighlight}
          />
        </h2>
        {showOnlineDesc
          ? item.OnlineDesc && isInternalUser && <span className='text-xs text-metal-500'>{item.OnlineDesc}</span>
          : item.FuzzySearch && (
              <span className='text-xs text-metal-500'>
                <SearchFriendResultDescription
                  id={item.ID}
                  fuzzySearch={item.FuzzySearch}
                  showNickname={showNicknameInDescription}
                />
              </span>
            )}
      </div>
      {suffix}
      {arrow && (
        <ArrowRight2
          className='size-5'
          variant='Linear'
        />
      )}
    </div>
  );
}
